import { useState, useEffect, useCallback } from 'react'
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import type { TableColumn } from '@/types/compatibility'

interface FilterCondition {
  id: string
  column: string
  operator: string
  value: string
  logicalOperator?: 'AND' | 'OR'
}

interface FilterModalProps {
  isOpen: boolean
  onClose: () => void
  onApply: (whereClause: string) => void
  columns: TableColumn[]
  initialFilter?: string
}

const OPERATORS = [
  { value: '=', label: 'Equals (=)' },
  { value: '!=', label: 'Not Equals (!=)' },
  { value: '>', label: 'Greater Than (>)' },
  { value: '<', label: 'Less Than (<)' },
  { value: '>=', label: 'Greater or Equal (>=)' },
  { value: '<=', label: 'Less or Equal (<=)' },
  { value: 'LIKE', label: 'Contains (LIKE)' },
  { value: 'NOT LIKE', label: 'Does not contain (NOT LIKE)' },
  { value: 'IS NULL', label: 'Is NULL' },
  { value: 'IS NOT NULL', label: 'Is not NULL' },
  { value: 'IN', label: 'In (comma separated)' },
  { value: 'NOT IN', label: 'Not In (comma separated)' },
]

export function FilterModal({ isOpen, onClose, onApply, columns, initialFilter }: FilterModalProps) {
  const [conditions, setConditions] = useState<FilterCondition[]>([])

  const addCondition = useCallback(() => {
    const newCondition: FilterCondition = {
      id: Date.now().toString(),
      column: columns[0]?.name || '',
      operator: '=',
      value: '',
      logicalOperator: conditions.length > 0 ? 'AND' : undefined
    }
    setConditions([...conditions, newCondition])
  }, [columns, conditions])

  useEffect(() => {
    if (isOpen && conditions.length === 0) {
      // Add initial condition
      addCondition()
    }
  }, [isOpen, addCondition, conditions.length])

  // Parse initial filter if provided (basic parsing for simple conditions)
  useEffect(() => {
    if (initialFilter && conditions.length === 0) {
      // For now, just add an empty condition - users can modify in the builder
      addCondition()
    }
  }, [initialFilter, addCondition, conditions.length])

  const removeCondition = (id: string) => {
    const newConditions = conditions.filter(c => c.id !== id)
    // Remove logical operator from first condition if it exists
    if (newConditions.length > 0 && newConditions[0].logicalOperator) {
      newConditions[0] = { ...newConditions[0], logicalOperator: undefined }
    }
    setConditions(newConditions)
  }

  const updateCondition = (id: string, updates: Partial<FilterCondition>) => {
    setConditions(conditions.map(c => 
      c.id === id ? { ...c, ...updates } : c
    ))
  }

  const buildWhereClause = (): string => {
    const validConditions = conditions.filter(c => c.column && c.operator)
    if (validConditions.length === 0) return ''

    return validConditions.map((condition, index) => {
      let clause = ''
      
      // Add logical operator for non-first conditions
      if (index > 0 && condition.logicalOperator) {
        clause += ` ${condition.logicalOperator} `
      }

      // Handle NULL operators
      if (condition.operator === 'IS NULL' || condition.operator === 'IS NOT NULL') {
        clause += `${condition.column} ${condition.operator}`
      }
      // Handle IN operators
      else if (condition.operator === 'IN' || condition.operator === 'NOT IN') {
        const values = condition.value.split(',').map(v => `'${v.trim()}'`).join(',')
        clause += `${condition.column} ${condition.operator} (${values})`
      }
      // Handle LIKE operators
      else if (condition.operator === 'LIKE' || condition.operator === 'NOT LIKE') {
        clause += `${condition.column} ${condition.operator} '%${condition.value}%'`
      }
      // Handle regular operators
      else {
        const isNumeric = isNumericColumn(condition.column)
        const quotedValue = isNumeric ? condition.value : `'${condition.value}'`
        clause += `${condition.column} ${condition.operator} ${quotedValue}`
      }

      return clause
    }).join('')
  }

  const isNumericColumn = (columnName: string): boolean => {
    const column = columns.find(c => c.name === columnName)
    if (!column?.dataType) return false
    
    const dataType = column.dataType.toLowerCase()
    return dataType.includes('int') || 
           dataType.includes('decimal') || 
           dataType.includes('float') || 
           dataType.includes('double') ||
           dataType.includes('numeric')
  }

  const handleApply = () => {
    const whereClause = buildWhereClause()
    onApply(whereClause)
    onClose()
  }

  const handleClear = () => {
    setConditions([])
    onApply('')
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Advanced Filter - Query Builder
          </h2>
          <button type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-200px)] p-6">
          <div className="space-y-4">
            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                💡 <strong>Tip:</strong> Raw SQL filtering is now available in the main table view next to the search box for more flexibility.
              </p>
            </div>
            
            {conditions.map((condition, index) => (
              <div key={condition.id} className="flex items-center space-x-3 p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
                {/* Logical Operator */}
                {index > 0 && (
                  <select
                    value={condition.logicalOperator || 'AND'}
                    onChange={(e) => {
                      const val = e.target.value
                      updateCondition(condition.id, { logicalOperator: val === 'OR' ? 'OR' : 'AND' })
                    }}
                    className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800"
                  >
                    <option value="AND">AND</option>
                    <option value="OR">OR</option>
                  </select>
                )}

                {/* Column */}
                <select
                  value={condition.column}
                  onChange={(e) => { updateCondition(condition.id, { column: e.target.value }) }}
                  className="flex-1 border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-800"
                >
                  <option value="">Select Column</option>
                  {columns.map(column => (
                    <option key={column.name} value={column.name}>
                      {column.name} ({column.dataType})
                    </option>
                  ))}
                </select>

                {/* Operator */}
                <select
                  value={condition.operator}
                  onChange={(e) => { updateCondition(condition.id, { operator: e.target.value }) }}
                  className="flex-1 border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-800"
                >
                  {OPERATORS.map(op => (
                    <option key={op.value} value={op.value}>
                      {op.label}
                    </option>
                  ))}
                </select>

                {/* Value */}
                {!['IS NULL', 'IS NOT NULL'].includes(condition.operator) && (
                  <input
                    type="text"
                    value={condition.value}
                    onChange={(e) => { updateCondition(condition.id, { value: e.target.value }) }}
                    placeholder={
                      condition.operator.includes('IN') ? 'value1, value2, value3' :
                      condition.operator.includes('LIKE') ? 'search text' :
                      'value'
                    }
                    className="flex-1 border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-800"
                  />
                )}

                {/* Remove Button */}
                <button type="button"
                  onClick={() => { removeCondition(condition.id) }}
                  className="text-red-600 hover:text-red-800 p-1"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            ))}

            <button type="button"
              onClick={addCondition}
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 text-sm"
            >
              <PlusIcon className="w-4 h-4" />
              <span>Add Condition</span>
            </button>

            {/* Preview */}
            {conditions.length > 0 && (
              <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <h4 className="text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  Generated WHERE Clause:
                </h4>
                <code className="text-sm text-gray-600 dark:text-gray-400 break-all">
                  {buildWhereClause() || 'No valid conditions'}
                </code>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
          <button type="button"
            onClick={handleClear}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 rounded-md"
          >
            Clear All
          </button>
          
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 rounded-md"
            >
              Cancel
            </button>
            <button type="button"
              onClick={handleApply}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
            >
              Apply Filter
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
